import { useEffect, useState } from "react";
import { Pencil, FileEdit, History, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EDITABLE_FIELDS = [
  ["owner_name", "Owner name"], ["owner_phone", "Contact number"], ["owner_email", "Email"],
  ["mailing_address", "Mailing address"], ["brand", "Vehicle brand"], ["model", "Vehicle model"],
  ["colour", "Colour"], ["chassis_no", "Chassis number"], ["panels_coverage", "Coverage"],
  ["panels_notes", "Coverage notes"], ["area_sqft", "Film area (sq.ft.)"],
  ["install_date", "Install date"], ["serial_number", "Serial number"],
  ["film_type", "Film type"], ["warranty_years", "Warranty years"],
];

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-crimson-100 text-crimson-700",
};

// CRM-only actions on a warranty record: edit plate (no approval), request field edit, history.
export const WarrantyEditActions = ({ warranty, onChanged }) => {
  const { user } = useAuth();
  const canEdit = user.role === "crm";
  const canDirectEdit = ["network_admin", "ceo", "crm"].includes(user.role);
  const [plateOpen, setPlateOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ef, setEf] = useState({});
  const [plate, setPlate] = useState(warranty.reg_no || "");
  const [req, setReq] = useState({ field: "", new_value: "", reason: "" });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const openEdit = () => {
    setEf({
      customer_name: warranty.customer_name || warranty.owner_name || "",
      contact: warranty.customer_contact || warranty.owner_phone || "",
      email: warranty.email || warranty.owner_email || "",
      studio_name: warranty.studio_name || "",
      warranty_type: warranty.warranty_type || "",
      vehicle: warranty.vehicle || [warranty.brand, warranty.model].filter(Boolean).join(" "),
      registration_no: warranty.reg_no || warranty.registration_no || "",
      serial_number: warranty.serial_number || "",
      start_date: (warranty.start_date || "").slice(0, 10),
      duration_months: warranty.duration_months ?? "",
      notes: warranty.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!ef.customer_name?.trim()) return toast.error("Customer name is required");
    setBusy(true);
    try {
      const payload = { ...ef };
      if (payload.duration_months === "" || payload.duration_months == null) delete payload.duration_months;
      else payload.duration_months = Number(payload.duration_months);
      await api.put(`/warranties/${warranty.id}`, payload);
      toast.success("Warranty updated");
      setEditOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const savePlate = async () => {
    if (!plate.trim()) return toast.error("Enter the new number plate");
    setBusy(true);
    try {
      await api.put(`/warranties/${warranty.id}/plate`, { reg_no: plate.trim() });
      toast.success("Number plate updated");
      setPlateOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitReq = async () => {
    if (!req.field) return toast.error("Pick a field to change");
    if (!req.reason.trim()) return toast.error("Add a reason");
    setBusy(true);
    try {
      await api.post(`/warranties/${warranty.id}/edit-requests`, req);
      toast.success("Edit request sent to the CEO for approval");
      setReqOpen(false);
      setReq({ field: "", new_value: "", reason: "" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    setHistOpen(true);
    try {
      const { data } = await api.get(`/warranties/${warranty.id}/edit-log`);
      setLog(data.edit_log || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!canEdit && !canDirectEdit) return null;

  return (
    <div className="flex gap-1">
      {canDirectEdit && (
        <Button size="sm" variant="outline" title="Edit warranty details"
          data-testid={`edit-warranty-${warranty.id}`} onClick={openEdit}>
          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
        </Button>
      )}
      {canEdit && (
        <Button size="sm" variant="outline" title="Edit number plate"
          data-testid={`edit-plate-${warranty.id}`} onClick={() => { setPlate(warranty.reg_no || ""); setPlateOpen(true); }}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      {canEdit && (
        <Button size="sm" variant="outline" title="Request field edit"
          data-testid={`request-edit-${warranty.id}`} onClick={() => setReqOpen(true)}>
          <FileEdit className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button size="sm" variant="outline" title="Edit history"
        data-testid={`edit-history-${warranty.id}`} onClick={openHistory}>
        <History className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="edit-warranty-dialog">
          <DialogHeader><DialogTitle>Edit warranty details</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Authorised roles can edit these fields directly — every change is logged.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ["customer_name", "Customer name *"], ["contact", "Contact"], ["email", "Email"],
              ["studio_name", "Installing detailer / studio"], ["warranty_type", "Warranty type"],
              ["vehicle", "Vehicle"], ["registration_no", "Registration no."],
              ["serial_number", "Serial number"], ["duration_months", "Duration (months)"],
              ["notes", "Notes"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input className="mt-1" data-testid={`edit-warranty-${k}`} value={ef[k] ?? ""}
                  onChange={(e) => setEf((s) => ({ ...s, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Registration date</Label>
              <Input type="date" className="mt-1" data-testid="edit-warranty-start_date"
                value={ef.start_date ?? ""} onChange={(e) => setEf((s) => ({ ...s, start_date: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="save-warranty-edit-btn" disabled={busy} onClick={saveEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={plateOpen} onOpenChange={setPlateOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="edit-plate-dialog">
          <DialogHeader><DialogTitle>Edit number plate</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Plates are often temporary at registration — update anytime, no approval needed. The change is logged.</p>
          <Input className="mt-2" data-testid="plate-input" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="New number plate" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlateOpen(false)}>Cancel</Button>
            <Button data-testid="save-plate-btn" disabled={busy} onClick={savePlate}>Save plate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="sm:max-w-md" data-testid="request-edit-dialog">
          <DialogHeader><DialogTitle>Request a warranty edit</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Any field other than the number plate needs CEO approval.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Field</Label>
              <Select value={req.field} onValueChange={(v) => setReq((r) => ({ ...r, field: v }))}>
                <SelectTrigger className="mt-1" data-testid="request-field-select"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent>
                  {EDITABLE_FIELDS.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">New value</Label>
              <Input className="mt-1" data-testid="request-value-input" value={req.new_value} onChange={(e) => setReq((r) => ({ ...r, new_value: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Textarea className="mt-1" rows={2} data-testid="request-reason-input" value={req.reason} onChange={(e) => setReq((r) => ({ ...r, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
            <Button data-testid="submit-edit-request-btn" disabled={busy} onClick={submitReq}>Send for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-history-dialog">
          <DialogHeader><DialogTitle>Edit history — {warranty.certificate_no}</DialogTitle></DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {log.length === 0 && <p className="py-4 text-center text-sm text-slate-400">No edits yet.</p>}
            {log.map((e, i) => (
              <div key={i} className="rounded-lg border border-brand-line p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-brand-ink">{e.field_label || e.field}</span>
                  {e.status && <Badge className={`border-0 ${STATUS_BADGE[e.status] || ""}`}>{e.status}</Badge>}
                  {e.type === "plate_update" && <Badge className="border-0 bg-blue-100 text-blue-700">plate</Badge>}
                </div>
                <p className="text-slate-600">
                  {e.old_value ? `${e.old_value} → ` : ""}<b>{e.new_value}</b>
                </p>
                {e.reason && <p className="text-xs text-slate-500">Reason: {e.reason}</p>}
                {e.decision_note && <p className="text-xs text-slate-500">Note: {e.decision_note}</p>}
                <p className="mt-1 text-xs text-slate-400">{e.by_name} · {fmtDate(e.at)}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// CEO approvals panel for pending warranty edit requests.
export const WarrantyEditApprovals = () => {
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState({});
  const load = () => api.get("/warranty-edit-requests", { params: { status: "pending" } })
    .then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const decide = async (r, approve) => {
    try {
      await api.post(`/warranty-edit-requests/${r.id}/${approve ? "approve" : "reject"}`, { note: note[r.id] || "" });
      toast.success(approve ? "Approved — CRM & studio notified" : "Rejected — CRM notified");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400" data-testid="no-pending-edits">No pending warranty edit requests.</p>;
  }
  return (
    <div className="space-y-3" data-testid="warranty-approvals-list">
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg border border-brand-line p-4" data-testid={`approval-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium text-brand-ink">
              {r.certificate_no} · {r.field_label}
            </p>
            <span className="text-xs text-slate-400">by {r.requested_by_name}</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {r.old_value ? `${r.old_value} → ` : ""}<b>{r.new_value}</b>
          </p>
          <p className="text-xs text-slate-500">Reason: {r.reason}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" placeholder="Optional note" data-testid={`approval-note-${r.id}`}
              value={note[r.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))} />
            <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => decide(r, true)}>
              <Check className="mr-1 h-4 w-4" /> Approve
            </Button>
            <Button size="sm" variant="outline" data-testid={`reject-${r.id}`} onClick={() => decide(r, false)}>
              <X className="mr-1 h-4 w-4" /> Reject
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
